import { Core } from "@faire/design-tokens";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { getSpacing, GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import range from "lodash/range";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { GhostBlock } from "@faire/retailer/components/GhostLoading/GhostBlock";
import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";

interface IProps {
  onlyShowProductTiles?: boolean;
}

export const HomeSkeleton: React.FC<IProps> = ({ onlyShowProductTiles }) => (
  <PageWrapper data-test-id="home-skeleton">
    {!onlyShowProductTiles ? <GhostSearchTiles /> : null}
    <GhostProductTiles />
    <GhostProductTiles />
  </PageWrapper>
);

const GhostSearchTiles: React.FC = () => (
  <SearchTilesWrapper>
    <GhostBlock $width="200px" $height="32px" />
    <Spacer height={4} />
    <GhostBlock $width="140px" $height="20px" />
    <Spacer height="2x" />
    <Row style={{ overflow: "hidden" }}>
      {range(0, 8).map((index) => (
        <React.Fragment key={index}>
          {index === 0 ? null : (
            <Spacer width={{ mobileAndAbove: "2x", tabletAndAbove: "4x" }} />
          )}
          <Column align="center">
            <img
              src={EAEAEA_PIXEL}
              width="88px"
              height="88px"
              style={{ borderRadius: "50%" }}
            />
            <Spacer height="1x" />
            <GhostBlock $width="64px" $height="20px" />
          </Column>
        </React.Fragment>
      ))}
    </Row>
  </SearchTilesWrapper>
);

const GhostProductTilesBase: React.FC = () => {
  const { isMobile } = useViewport();
  return (
    <ProductTilesWrapper>
      <Row align="baseline">
        <GhostBlock $width="180px" $height="32px" />
        {isMobile ? null : (
          <>
            <GrowingSpacer />
            <GhostBlock $width="120px" $height="20px" />
          </>
        )}
      </Row>
      <Spacer height="2x" />
      <Grid>
        {range(0, isMobile ? 4 : 5).map((index) => (
          <Column
            style={{ border: `1px solid ${Core.border.muted}` }}
            key={index}
          >
            <AspectRatioPicture $width="100%" $ratio={1} $hideBorder>
              <img src={EAEAEA_PIXEL} />
            </AspectRatioPicture>
            <Column style={{ padding: getSpacing("2x") }}>
              <GhostBlock $width="120px" $height="20px" />
              <Spacer height="1x" />
              <GhostBlock $width="80px" $height="20px" />
            </Column>

            <HorizontalLine color={Core.border.muted} />
            <Column style={{ padding: getSpacing("2x") }}>
              <GhostBlock $width="120px" $height="20px" />
              <Spacer height="1x" />
              <GhostBlock $width="80px" $height="20px" />
            </Column>
          </Column>
        ))}
      </Grid>
      {isMobile ? (
        <>
          <Spacer height="2x" />
          <GhostBlock
            $width="120px"
            $height="20px"
            style={{ alignSelf: "center" }}
          />
        </>
      ) : null}
    </ProductTilesWrapper>
  );
};

const GhostProductTiles = observer(GhostProductTilesBase);

const PageWrapper = styled(Column)`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  cursor: progress;
`;

export const SearchTilesWrapper = styled(Column)`
  padding: ${getSpacing("3x", 0, "2x", "2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("3x", "7x", "2x")};
  }
`;

export const ProductTilesWrapper = styled(Column)`
  padding: ${getSpacing("2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("3x", "7x")};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, calc(50% - 4px));
  gap: ${getSpacing("1x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template-columns: repeat(5, 1fr);
  }
`;
